import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import appConfig from '@/config';
import { FeatureFlag } from '@/types/feature_flags';

import { initStore } from './store-utils';

export const useFeatureFlagStore = defineStore('FeatureFlags', () => {
  type Flag = {
    name: FeatureFlag;
  };

  const featureFlags = ref<Flag[]>([]);

  const { currentOrg, currentUser } = useAuth();

  const { initError, ready, readySync, running, refresh } = initStore(
    async () => {
      if (!currentUser.value.id || !currentOrg.value.id) return;

      const { data, error } = await crossbeamApi.GET('/v0.1/feature-flags');

      if (error) throw error;

      featureFlags.value = (data?.items as Flag[]) ?? [];

      const lsFlagInfo = localStorage.getItem('featureFlagInfo');

      if (lsFlagInfo && appConfig.env !== 'prod') {
        type ExtFlag = Flag & { enabled?: boolean };
        const { enabled, append, flags } = JSON.parse(lsFlagInfo) as {
          enabled: boolean;
          append: boolean;
          flags: ExtFlag[];
        };
        if (enabled && Array.isArray(flags)) {
          const active = flags
            .filter((f) => f.enabled)
            .map((f) => ({ name: f.name }));
          const managed = new Set(flags.map((f) => f.name));
          if (!append) {
            featureFlags.value = active;
          } else {
            const kept = featureFlags.value.filter((f) => !managed.has(f.name));
            featureFlags.value = [...kept, ...active];
          }
        }
      }
    },
  );

  refresh({ useCache: true });

  /* The boot refresh exits early (yet is marked ready) if it runs before an org
     id exists - e.g. the store initializes before loadUserProfile resolves the
     org, or the org is set in-app (seat-request approval, org creation). Re-fetch
     when the org id becomes available. The getter is null-safe because some unit
     tests mock useAuth without currentOrg. */
  watch(
    () => currentOrg?.value?.id,
    (orgId) => {
      if (orgId) refresh();
    },
  );

  function hasFeatureFlag(flag: FeatureFlag) {
    return featureFlags.value.some((o) => o.name === flag);
  }

  return {
    error: initError,
    ready,
    readySync,
    running,
    refreshFeatureFlagStore: refresh,
    featureFlags,
    hasFeatureFlag,
  };
});
